import PropTypes from 'prop-types';
import {
  useDataModel, params, string, shape
} from '@thd-nucleus/data-sources';

/**
 * Custom hook that fetches product data needed to resolve an intent.
 * Retrieves product identifiers (omsId and sku) for intent-type products.
 *
 * @param {Object} params - The parameters object
 * @param {string} params.itemId - The item ID of the product to fetch
 * @param {boolean} params.isIntent - Flag indicating if this is an intent-type product
 * @returns {Object} An object containing the product identifiers
 * @returns {string} returns.omsId - The OMS item ID from product identifiers
 * @returns {string} returns.sku - The SKU (storeSkuNumber, omsThdSku, or specialOrderSku)
 */
export const useIntentProductData = ({ itemId, isIntent }) => {
  const { data, error } = useDataModel('product', {
    variables: {
      itemId
    },
    skip: !itemId && !isIntent
  });
  const { product } = data || {};

  const omsId = product?.identifiers?.itemId;
  const storeSkuNumber = product?.identifiers?.storeSkuNumber;
  const omsThdSku = product?.identifiers?.omsThdSku;
  const specialOrderSku = product?.identifiers?.specialOrderSku;
  const sku = storeSkuNumber || omsThdSku || specialOrderSku;

  return { omsId, sku, product };
};

useIntentProductData.PropTypes = {
  itemId: PropTypes.string.isRequired,
  isIntent: PropTypes.bool.isRequired
};

useIntentProductData.dataModel = {
  product: params({
    itemId: string().isRequired(),
    dataSource: string()
  }).shape({
    identifiers: shape({
      itemId: string(),
      storeSkuNumber: string(),
      omsThdSku: string(),
      specialOrderSku: string()
    }),
  })
};
